/**
 * NewProjectModal — inline project creation from the invoice page
 *
 * Opened via the "+" button in the project selector on InvoiceCreatePage.
 * On success, the new project is auto-selected in the invoice form and
 * a toast is shown if a permit reminder was created.
 */
import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateProject } from '@/hooks/useProjects';
import { useProjectCategoryOptions } from '@/hooks/useProjectCategories';
import { usePermitTypes } from '@/hooks/usePermits';
import { SearchableOrganizationCombobox } from '@/components/organizations/SearchableOrganizationCombobox';
import { OrganizationFormModal } from '@/components/forms/OrganizationFormModal';
import { RenewalFields } from '@/components/RenewalFields';
import {
  formatExpiryDate,
  renewalNeedsExpiry,
  renewalNotSavedMessage,
} from '@/lib/renewalResult';

// Spec keystone-client-required-per-category (R9):
// Client is required when the selected category has client_required=TRUE.
// Use a superRefine so the message can reference the client_required rule explicitly.
const schema = z.object({
  name: z.string().min(1, 'Project name is required'),
  organization_id: z.string().optional().default(''),
  category_id: z.string().optional(),
  _clientRequired: z.boolean().optional(),
}).superRefine((data, ctx) => {
  if (data._clientRequired && !data.organization_id) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['organization_id'],
      message: 'Client is required for this category',
    });
  }
});

type FormData = z.infer<typeof schema>;

export interface NewProjectModalProps {
  open: boolean;
  onClose: () => void;
  onProjectCreated: (project: { id: string; name: string }) => void;
}

export function NewProjectModal({ open, onClose, onProjectCreated }: NewProjectModalProps) {
  const createProject = useCreateProject();
  const { data: categoryOptions } = useProjectCategoryOptions();
  const { data: permitTypesData } = usePermitTypes();
  const permitTypeDefinitions = permitTypesData?.permit_types?.filter(t => t.is_active) ?? [];

  const [showCreateOrganization, setShowCreateOrganization] = useState(false);

  // Renewal state — only relevant when selected category has renewal_tracking_enabled
  const [renewalPermitTypeDefinitionId, setRenewalPermitTypeDefinitionId] = useState('');
  const [renewalExpiryDate, setRenewalExpiryDate] = useState('');
  const [renewalRecurringPeriod, setRenewalRecurringPeriod] = useState('');
  const [renewalReminderDays, setRenewalReminderDays] = useState<number[]>([30, 14]);
  // v0.17.16: shown when renewal details are filled in without an expiry date.
  const [renewalError, setRenewalError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', organization_id: '', category_id: undefined },
  });

  const selectedCategoryId = watch('category_id');
  const selectedCategory = categoryOptions?.find(c => c.id === selectedCategoryId);
  const renewalEnabled = selectedCategory?.renewal_tracking_enabled ?? false;
  // R9: derive client_required from selected category (falsy defaults safely to permissive)
  const clientRequired = (selectedCategory as any)?.client_required ?? false;

  // Keep the hidden client_required flag synced for Zod refine
  useEffect(() => {
    setValue('_clientRequired' as any, clientRequired);
  }, [clientRequired, setValue]);

  // Reset when modal opens/closes
  useEffect(() => {
    if (open) {
      reset({ name: '', organization_id: '', category_id: undefined });
      setRenewalPermitTypeDefinitionId('');
      setRenewalExpiryDate('');
      setRenewalRecurringPeriod('');
      setRenewalReminderDays([30, 14]);
    }
  }, [open, reset]);

  // Pre-fill reminder days from category defaults when category changes
  useEffect(() => {
    if (selectedCategory?.renewal_tracking_enabled) {
      const catDefaults = selectedCategory.default_reminder_days;
      if (Array.isArray(catDefaults) && catDefaults.length > 0) {
        setRenewalReminderDays(catDefaults);
      } else {
        setRenewalReminderDays([30, 14]);
      }
      setRenewalPermitTypeDefinitionId('');
      setRenewalExpiryDate('');
      setRenewalRecurringPeriod('');
    }
    setRenewalError(null);
  }, [selectedCategoryId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (data: FormData) => {
    const category = categoryOptions?.find(c => c.id === data.category_id);

    if (
      renewalEnabled &&
      renewalNeedsExpiry({
        expiryDate: renewalExpiryDate,
        permitTypeDefinitionId: renewalPermitTypeDefinitionId,
        recurringPeriod: renewalRecurringPeriod,
      })
    ) {
      setRenewalError('Add the expiry date, or clear the renewal.');
      return;
    }
    setRenewalError(null);

    const renewalData = (renewalEnabled && renewalExpiryDate)
      ? {
          // v0.17.16: blank means "none", never "" (an empty string is not a
          // valid permit type id and used to make the renewal fail to save).
          permit_type_definition_id: renewalPermitTypeDefinitionId || null,
          expiry_date: renewalExpiryDate,
          recurring_period: renewalRecurringPeriod || null,
          reminder_days: renewalReminderDays,
        }
      : null;

    try {
      const result = await createProject.mutateAsync({
        name: data.name,
        organization_id: data.organization_id || null,
        category: category?.value,
        workflow_stage: 'project_setup',
        renewal_data: renewalData,
        // R9: fast-path permissiveness — invoice flow allows creating a project
        // without a client (matches proposal semantics). Backend R2c still enforces
        // client_required=TRUE as defense-in-depth.
        allow_client_deferred: true,
      } as any);

      const newProject = result?.project ?? result;

      onProjectCreated({ id: newProject.id, name: newProject.name });

      if (result.permit_created) {
        toast.success(`Project created. Renewal reminder set for ${formatExpiryDate(renewalExpiryDate)}.`);
      }
      // v0.17.16: the project saved but its renewal did not. Say so.
      const renewalWarning = renewalNotSavedMessage(!!renewalData, result);
      if (renewalWarning) {
        toast.warning(renewalWarning, { duration: Infinity });
      }

      onClose();
    } catch {
      // Error toast shown by useCreateProject; keep modal open
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent
          className="max-h-[92dvh] w-[calc(100vw-1rem)] max-w-lg overflow-y-auto p-4 sm:p-6"
          data-testid="new-project-modal"
        >
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
            <DialogDescription>
              The new project will be auto-selected in your invoice.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-2">
            {/* Project Name */}
            <div>
              <Label htmlFor="new-project-name">Project Name *</Label>
              <Input
                id="new-project-name"
                {...register('name')}
                placeholder="e.g. 1234 Main St – Renewal"
                autoFocus
              />
              {errors.name && (
                <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
              )}
            </div>

            {/* Client — required iff category.client_required */}
            <div>
              <Label>{clientRequired ? 'Client *' : 'Client (optional)'}</Label>
              <Controller
                name="organization_id"
                control={control}
                render={({ field }) => (
                  <SearchableOrganizationCombobox
                    value={field.value}
                    onValueChange={field.onChange}
                    role="client"
                    placeholder="Select client"
                    useServerSearch
                    onCreateNew={() => setShowCreateOrganization(true)}
                  />
                )}
              />
              {errors.organization_id && (
                <p className="text-sm text-red-600 mt-1">{errors.organization_id.message}</p>
              )}
            </div>

            {/* Category */}
            <div>
              <Label>Category *</Label>
              <Controller
                name="category_id"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value ?? ''}
                    onValueChange={(val) => field.onChange(val || undefined)}
                  >
                    <SelectTrigger data-testid="category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {(categoryOptions ?? [])
                        .filter(c => c.is_active)
                        .map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.value}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* Renewal fields — shown only for renewal-enabled categories */}
            {renewalEnabled && (
              <div className="rounded-lg border p-4 bg-muted/30 space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Renewal Details</p>
                <RenewalFields
                  permitTypeDefinitions={permitTypeDefinitions}
                  permitTypeDefinitionId={renewalPermitTypeDefinitionId}
                  onPermitTypeDefinitionIdChange={setRenewalPermitTypeDefinitionId}
                  expiryDate={renewalExpiryDate}
                  onExpiryDateChange={(d) => {
                    setRenewalExpiryDate(d);
                    if (d) setRenewalError(null);
                  }}
                  recurringPeriod={renewalRecurringPeriod}
                  onRecurringPeriodChange={setRenewalRecurringPeriod}
                  reminderDays={renewalReminderDays}
                  onReminderDaysChange={setRenewalReminderDays}
                />
                {renewalError && (
                  <p className="text-sm text-destructive" role="alert" data-testid="renewal-expiry-error">
                    {renewalError}
                  </p>
                )}
              </div>
            )}
          </form>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={createProject.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createProject.isPending}
              onClick={handleSubmit(onSubmit)}
            >
              {createProject.isPending ? 'Creating…' : 'Create Project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OrganizationFormModal
        open={showCreateOrganization}
        onOpenChange={setShowCreateOrganization}
        fieldSet="minimal"
        onSave={(org) => setValue('organization_id', org.id)}
      />
    </>
  );
}
